import { Fragment, useEffect, useState } from "react";

import { PageContainer, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useLocation, useParams } from "react-router-dom";

import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import { PricingCardsSection } from "../components/PricingCardsSection/PricingCardsSection";
import { PurchaseNotice } from "../components/PurchaseNotice/PurchaseNotice";
import { IndividualSubscriptionInfo } from "../components/TeamSubscriptions/IndividualSubscriptionInfo/IndividualSubscriptionInfo";
import { PaddleOneTimeSubscriptionInfo } from "../components/TeamSubscriptions/PaddleOneTimeSubscriptionInfo/PaddleOneTimeSubscriptionInfo";
import { SeatBasedSubscriptionInfo } from "../components/TeamSubscriptions/SeatBasedSubscriptionInfo/SeatBasedSubscriptionInfo";
import { UpgradeToStudioModal } from "../components/TeamSubscriptions/UpgradeToStudioModal/UpgradeToStudioModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import { WelcomeModal } from "../components/WelcomeModal/WelcomeModal";
import { useChinaUser } from "../hooks/useChinaUser";
import { useHasStudioPricing } from "../hooks/usePriceConfig";
import { useTeamSubscriptions } from "../hooks/useTeams";
import type { SeatBasedTeamSubscription } from "../hooks/useTeamsTypes";
import useTranslation from "../hooks/useTranslation";
import { SubscriptionTier, SubscriptionType, type URLState, URLStateActions } from "../types";

const TeamSubscriptionPage = () => {
  const [upgradeSubscription, setUpgradeSubscription] = useState<SeatBasedTeamSubscription | null>(
    null
  );
  const { isStudioTier, isPending } = useHasStudioPricing();

  const { t, m } = useTranslation();
  const { teamID } = useParams();
  const { isChinaUser, isLoading: isChinaUserLoading } = useChinaUser();

  const { data: teamSubscriptionData, isLoading: isTeamSubscriptionLoading } =
    useTeamSubscriptions(teamID);

  const { state } = useLocation();
  const [historyState, setHistoryState] = useState<URLState | null>(null);

  useEffect(() => {
    if (state) {
      setHistoryState(state);
    }
  }, [state]);

  useEffect(() => {
    if (!historyState || !teamSubscriptionData) {
      return;
    }

    if (historyState.Action === URLStateActions.OpenUpgradeToStudioModal) {
      const { seatBasedTeamSubscription } = teamSubscriptionData;
      if (seatBasedTeamSubscription?.tier === SubscriptionTier.Individual) {
        setUpgradeSubscription(seatBasedTeamSubscription);
      }
    }

    setHistoryState(null);
  }, [historyState, teamSubscriptionData]);

  if (
    isChinaUserLoading ||
    isTeamSubscriptionLoading ||
    !teamSubscriptionData ||
    !teamID ||
    isPending
  ) {
    return <SpinnerCenter />;
  }

  const hasNoSubscription =
    !teamSubscriptionData.creditBasedTeamSubscription &&
    !teamSubscriptionData.seatBasedTeamSubscription &&
    !teamSubscriptionData.individualSubscriptions;

  return (
    <UnderMaintenanceOrChildren>
      <PageContainer
        title={
          hasNoSubscription && isStudioTier
            ? t(m.TeamSubscriptionPage.StudioPricingTitle)
            : isChinaUser
              ? t(m.TeamSubscriptionPage.LicenseTitle)
              : t(m.TeamSubscriptionPage.SubscriptionTitle)
        }
      >
        {hasNoSubscription && (
          <>
            {!isStudioTier && (
              <>
                <CTARow
                  title={
                    isChinaUser
                      ? t(m.TeamSubscriptionPage.LicenseTitle)
                      : t(m.TeamSubscriptionPage.SubscriptionTitle)
                  }
                  text={t(m.TeamSubscriptionPage.BasicPlanText)}
                />
                <CTARowDivider />
              </>
            )}
            {isStudioTier ? (
              <PricingCardsSection />
            ) : (
              <PurchaseNotice isChinaVariant={isChinaUser} />
            )}
          </>
        )}

        {teamSubscriptionData.individualSubscriptions && (
          <>
            {teamSubscriptionData.individualSubscriptions.map((subscription) => (
              <Fragment key={subscription.id}>
                {subscription.type === SubscriptionType.PaddleOneTime ? (
                  <PaddleOneTimeSubscriptionInfo subscription={subscription} />
                ) : (
                  <IndividualSubscriptionInfo subscription={subscription} />
                )}
                <CTARowDivider />
              </Fragment>
            ))}
          </>
        )}

        {teamSubscriptionData.seatBasedTeamSubscription && (
          <SeatBasedSubscriptionInfo
            subscription={teamSubscriptionData.seatBasedTeamSubscription}
            teamID={teamID}
          />
        )}

        {teamSubscriptionData.creditBasedTeamSubscription &&
          // NOTE: We are not rendering it yet, since they are only members of the team, and currently we are only showing the members page for them
          null}
        <WelcomeModal />
        {upgradeSubscription && (
          <UpgradeToStudioModal
            open
            onClose={() => setUpgradeSubscription(null)}
            subscription={upgradeSubscription}
            teamID={teamID}
          />
        )}
      </PageContainer>
    </UnderMaintenanceOrChildren>
  );
};

export default TeamSubscriptionPage;
